/**
 * Поиск позиции номенклатуры — input с выпадающим списком совпадений.
 * Используется при добавлении строки в накладную.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchItems, type ItemRow } from "@/api/warehouse";


interface Props {
  warehouse?: string;
  placeholder?: string;
  onSelect: (item: ItemRow) => void;
  initial?: string;
  width?: number | string;
}


export function ItemAutocomplete({ warehouse, onSelect, placeholder = "Поиск по артикулу или наименованию", initial = "", width = "100%" }: Props) {
  const [value, setValue] = useState(initial);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Debounce 200ms
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 200);
    return () => clearTimeout(t);
  }, [value]);

  const q = useQuery({
    queryKey: ["warehouse-items-search", warehouse, debounced],
    queryFn: () => fetchItems({ warehouse, search: debounced, limit: 30 }),
    enabled: debounced.trim().length >= 2,
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const items = q.data?.items || [];

  function pick(it: ItemRow) {
    onSelect(it);
    setValue("");
    setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", width }}>
      <input
        type="text"
        className="input-sm"
        value={value}
        placeholder={placeholder}
        onChange={(e) => { setValue(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter" && items.length > 0) pick(items[0]);
        }}
        style={{ width: "100%", fontSize: 12 }}
      />
      {open && debounced.trim().length >= 2 && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 50,
          background: "var(--card)", border: "1px solid var(--line)", borderRadius: 4,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)", maxHeight: 280, overflowY: "auto",
        }}>
          {q.isLoading && <div style={{ padding: 8, fontSize: 12, color: "var(--mute)" }}>Поиск…</div>}
          {!q.isLoading && items.length === 0 && (
            <div style={{ padding: 8, fontSize: 12, color: "var(--mute)" }}>Ничего не найдено</div>
          )}
          {items.map(it => (
            <button
              key={it.id}
              type="button"
              onClick={() => pick(it)}
              style={{
                display: "block", width: "100%", padding: "6px 10px",
                background: "transparent", border: "none", textAlign: "left",
                cursor: "pointer", fontSize: 12, borderBottom: "1px solid var(--line-soft)",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--paper)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <span className="tabular" style={{ color: "var(--mute)", minWidth: 80 }}>{it.sku || "—"}</span>
                <span style={{ flex: 1 }}>{it.name}</span>
                <span style={{ color: "var(--mute)" }}>{it.unit || ""}</span>
              </div>
              {it.section && (
                <div style={{ fontSize: 10, color: "var(--mute)", marginLeft: 88 }}>{it.section}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
